import React, { lazy, Suspense } from "react";
import SEO from "@/components/SEO";
import HeroSection from "@/components/HeroSection";
import CredentialsStrip from "@/components/CredentialsStrip";
import { useLang } from "@/i18n/LanguageContext";
import { provinces } from "@/data/provinces";
import { faqsCommon } from "@/data/faqs";
import { caseStudyBySlug } from "@/data/caseStudies";
import { COMPANY } from "@/lib/api";

// Sub-section components (split for SRP, readability and bundle splitting).
import { HomeBillAlert } from "@/components/home/HomeBillAlert";
import { HomeServices } from "@/components/home/HomeServices";
import { HomeTrustCounters } from "@/components/home/HomeTrustCounters";
import { HomeWhyUs } from "@/components/home/HomeWhyUs";
import { HomeEquipment } from "@/components/home/HomeEquipment";
import { HomeGallery } from "@/components/home/HomeGallery";
import { HomeCoverage } from "@/components/home/HomeCoverage";
import { HomeFAQ } from "@/components/home/HomeFAQ";

// Below-the-fold components: split into their own chunks so they do NOT bloat
// the LCP-critical main bundle. They load while the user reads the hero.
const ContactForm = lazy(() => import("@/components/ContactForm"));
const GoogleReviewsBanner = lazy(() => import("@/components/GoogleReviewsBanner"));
const VideoShowcase = lazy(() => import("@/components/VideoShowcase"));
const VideoProof = lazy(() => import("@/components/conversion/VideoProof"));
const CaseStudyHighlight = lazy(() => import("@/components/conversion/CaseStudyHighlight"));
const HomeToscanaMap = lazy(() => import("@/components/home/HomeToscanaMap"));
// Below-the-fold sections that pull the full comuni / premium-city datasets:
// lazy so the main bundle (LCP critical path) stays small.
const HomePriorityCities = lazy(() => import("@/components/home/HomePriorityCities"));
const HomePriorityServices = lazy(() => import("@/components/home/HomePriorityServices"));

// Skeleton placeholders avoid CLS by reserving height matching the real component.
function LazyBlockFallback({ minH = "120px" }) {
  return <div className="w-full" style={{ minHeight: minH }} aria-hidden="true" />;
}

function buildHomeJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://bagnoman.it/#business",
        name: COMPANY.name,
        alternateName: ["All Service Toscana", "Bagnoman", "Ricerca Perdite Toscana"],
        image: "https://bagnoman.it/favicon.png",
        logo: "https://bagnoman.it/favicon.png",
        url: "https://bagnoman.it",
        telephone: COMPANY.phone_display,
        email: COMPANY.email,
        priceRange: "\u20ac\u20ac",
        areaServed: provinces.map((p) => ({ "@type": "AdministrativeArea", name: p.name })),
        address: { "@type": "PostalAddress", streetAddress: "Via Guglielmo Marconi 33", addressLocality: "Massa e Cozzile", postalCode: "51010", addressRegion: "PT", addressCountry: "IT" },
        geo: { "@type": "GeoCoordinates", latitude: 43.8819, longitude: 10.7619 },
        openingHoursSpecification: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], opens: "00:00", closes: "23:59" },
        sameAs: [COMPANY.social.google_maps_search, COMPANY.social.google_business, COMPANY.social.facebook, COMPANY.social.instagram],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqsCommon.map((f) => ({
          "@type": "Question",
          name: f.q_it,
          acceptedAnswer: { "@type": "Answer", text: f.a_it },
        })),
      },
    ],
  };
}

function HomeContactCTA({ t }) {
  return (
    <section className="max-w-[1120px] mx-auto px-4 sm:px-6 mt-14 mb-10">
      <div className="p-5 sm:p-7 noise-bg rounded-xl border border-[hsl(var(--border))] bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div>
            <span className="inline-flex items-center text-xs font-semibold text-[hsl(var(--brand-aqua))] bg-[hsl(var(--brand-aqua))]/10 px-2.5 py-1 rounded-full">
              {t("common.h24")}
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold text-[hsl(var(--brand-navy))]">{t("contact_page.title")}</h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{t("contact_page.sub")}</p>
          </div>
          <Suspense fallback={<LazyBlockFallback minH="320px" />}>
            <ContactForm compact />
          </Suspense>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, lang } = useLang();

  return (
    <>
      <SEO
        title={lang === "it" ? "Ricerca perdite acqua H24 \u2014 senza demolizioni inutili" : "24/7 Water leak detection \u2014 without needless demolition"}
        description={lang === "it"
          ? "Bagnoman: ricerca perdite acqua, infiltrazioni e impianti antincendio in Toscana, La Spezia, Umbria e Alto Lazio. Intervento H24 con geofono, termocamera e gas tracciante."
          : "Bagnoman: water leak detection, infiltrations and fire systems in Tuscany, La Spezia, Umbria and Upper Lazio. 24/7 service with geophone, thermal camera and tracer gas."}
        path="/"
        lang={lang}
        jsonLd={buildHomeJsonLd()}
      />

      <HeroSection />

      <section className="max-w-[1120px] mx-auto px-4 sm:px-6 mt-6">
        <CredentialsStrip />
      </section>

      <section className="max-w-[1120px] mx-auto px-4 sm:px-6 mt-8">
        <Suspense fallback={<LazyBlockFallback minH="80px" />}>
          <GoogleReviewsBanner />
        </Suspense>
      </section>

      <HomeBillAlert t={t} />

      {/* NEW: real YouTube Shorts video + 4 on-site photos — strong above-mid social proof */}
      <Suspense fallback={<LazyBlockFallback minH="420px" />}>
        <VideoProof lang={lang} />
      </Suspense>

      {/* NEW: full case study Perugia with native MP4 video + 6 storyboard frames */}
      <Suspense fallback={<LazyBlockFallback minH="520px" />}>
        <CaseStudyHighlight cs={caseStudyBySlug("perugia-micro-perdita-villetta-ld6000")} lang={lang} />
      </Suspense>

      <HomeServices t={t} lang={lang} />
      <HomeTrustCounters t={t} lang={lang} />
      <HomeWhyUs t={t} />
      <HomeEquipment t={t} />

      <section className="max-w-[1120px] mx-auto px-4 sm:px-6 mt-14">
        <Suspense fallback={<LazyBlockFallback minH="240px" />}>
          <VideoShowcase />
        </Suspense>
      </section>

      <HomeGallery t={t} lang={lang} />
      {/* Recensioni: nessuna recensione/punteggio ospitato sul sito. Unica fonte:
          il profilo Google Business ufficiale (GoogleReviewsBanner sopra). */}
      {/* Interactive Tuscany map (compact): real ISTAT boundaries, no external APIs */}
      <Suspense fallback={<LazyBlockFallback minH="520px" />}>
        <HomeToscanaMap lang={lang} />
      </Suspense>
      <Suspense fallback={<LazyBlockFallback minH="420px" />}>
        <HomePriorityCities lang={lang} />
      </Suspense>
      <Suspense fallback={<LazyBlockFallback minH="320px" />}>
        <HomePriorityServices lang={lang} />
      </Suspense>
      <HomeCoverage t={t} lang={lang} />
      <HomeFAQ t={t} lang={lang} />
      <HomeContactCTA t={t} />
    </>
  );
}
